import React from 'react';

/**
 * Hamburger menu button with animated icon transition
 * 
 * Features:
 * - Transforms to X when menu is open
 * - Smooth animation
 * - Touch-friendly (44px min tap target)
 * - Accessible (aria-label, keyboard support)
 * 
 * Industry Pattern: Standard hamburger → X animation used by:
 * GitHub, Linear, Stripe, Vercel, Notion
 */

interface MobileMenuButtonProps {
  isOpen: boolean;
  onClick: () => void;
  className?: string;
  ariaLabel?: string;
}

export const MobileMenuButton: React.FC<MobileMenuButtonProps> = ({
  isOpen,
  onClick,
  className = '',
  ariaLabel = 'Toggle mobile menu'
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative w-11 h-11 flex items-center justify-center
        rounded-lg
        bg-white dark:bg-slate-800
        border-2 border-gray-300 dark:border-slate-600
        hover:bg-gray-50 dark:hover:bg-slate-700
        active:bg-gray-100 dark:active:bg-slate-600
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        ${className}
      `}
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      type="button"
    >
      <div className="w-5 h-4 flex flex-col justify-between">
        {/* Top line */}
        <span
          className={`
            block h-0.5 w-full bg-gray-700 dark:bg-gray-200
            transition-all duration-300 ease-in-out
            ${isOpen ? 'rotate-45 translate-y-1.5' : 'rotate-0 translate-y-0'}
          `}
        />
        
        {/* Middle line */}
        <span
          className={`
            block h-0.5 w-full bg-gray-700 dark:bg-gray-200
            transition-all duration-300 ease-in-out
            ${isOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}
          `}
        />
        
        {/* Bottom line */}
        <span
          className={`
            block h-0.5 w-full bg-gray-700 dark:bg-gray-200
            transition-all duration-300 ease-in-out
            ${isOpen ? '-rotate-45 -translate-y-1.5' : 'rotate-0 translate-y-0'}
          `}
        />
      </div>
    </button>
  );
};

